import { moveToFulfilled, moveToPending, moveToRejected, remove, update } from '@ms/yammer-data-support';
import { Reducer } from 'redux';

import {
  TopicsAllLoadFulfilledAction,
  TopicsAllLoadMoreFulfilledAction,
} from '../../domains/engageTopicsAll/actions.types';
import {
  SuggestedTopicsLoadFulfilledAction,
  SuggestedTopicsLoadMoreFulfilledAction,
} from '../../domains/suggestedTopics/actions.types';
import { FollowTopicAction, UnfollowTopicAction } from '../../domains/topicFollow/actions.types';
import { TopicHeaderLoadAction } from '../../domains/topicHeaderLoad/actions.types';
import { CreateCuratedTopicFulfilledAction } from '../../domains/topics/createCurateActions.types';
import { TopicEditAction } from '../../domains/topics/editActions.types';
import { RemoveCuratedTopicFulfilledAction } from '../../domains/topics/removeCuratedActions.types';
import { RemoveTopicFulfilledProps } from '../../domains/topics/removeTopicActions';
import { RemoveTopicFulfilledAction } from '../../domains/topics/removeTopicActions.types';
import {
  TopicsCuratedLoadFulfilledAction,
  TopicsCuratedLoadMoreFulfilledAction,
} from '../../domains/topicsCurated/actions.types';
import { TopicsFollowedLoadAction, TopicsFollowedLoadMoreAction } from '../../domains/topicsFollowedLoad/actions.types';
import {
  TrendingTopicsLoadFulfilledAction,
  TrendingTopicsLoadMoreFulfilledAction,
} from '../../domains/trendingTopicsLoad/actions.types';
import { TypeaheadFulfilledAction } from '../../domains/typeahead/actions.types';
import { Topic } from '../topics/types';

import { TopicFollowings } from './types';

export type TopicFollowingAction =
  | FollowTopicAction
  | TopicHeaderLoadAction
  | UnfollowTopicAction
  | CreateCuratedTopicFulfilledAction
  | TopicEditAction
  | TrendingTopicsLoadFulfilledAction
  | TrendingTopicsLoadMoreFulfilledAction
  | TopicsAllLoadFulfilledAction
  | TopicsAllLoadMoreFulfilledAction
  | TopicsCuratedLoadFulfilledAction
  | TopicsCuratedLoadMoreFulfilledAction
  | SuggestedTopicsLoadFulfilledAction
  | SuggestedTopicsLoadMoreFulfilledAction
  | RemoveCuratedTopicFulfilledAction
  | TopicsFollowedLoadAction
  | TopicsFollowedLoadMoreAction
  | TypeaheadFulfilledAction
  | RemoveTopicFulfilledAction;

const initialState: TopicFollowings = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

const followTopic = (state: TopicFollowings, topicId: Topic['id']) => {
  const topic = state.fulfilled.byId[topicId];

  if (!topic) {
    return state;
  }

  const followedTopic = {
    ...topic,
    viewerIsFollowing: true,
    followerCount: topic.followerCount + 1,
  };

  return update(state, topicId, followedTopic);
};

const unfollowTopic = (state: TopicFollowings, topicId: Topic['id']) => {
  const topic = state.fulfilled.byId[topicId];

  if (!topic) {
    return state;
  }

  const unfollowedTopic = {
    ...topic,
    viewerIsFollowing: false,
    followerCount: topic.followerCount - 1,
  };

  return update(state, topicId, unfollowedTopic);
};

const removeTopic = (state: TopicFollowings, topicIds: RemoveTopicFulfilledProps) => {
  const curatedTopicId = topicIds?.curatedTopicId;

  return curatedTopicId ? remove(state, [curatedTopicId]) : state;
};

const reducer: Reducer<TopicFollowings, TopicFollowingAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'TOPIC_HEADER.LOAD_PENDING':
      return moveToPending(state, [action.payload.topicId]);
    case 'TOPIC_HEADER.LOAD_REJECTED':
      return moveToRejected(state, [action.payload.topicId], action.payload.error);
    case 'TOPIC_HEADER.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicFollowings);
    case 'TRENDING_TOPICS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicFollowings);
    case 'TRENDING_TOPICS.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicFollowings);
    case 'TOPICS_FOLLOWED.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicFollowings);
    case 'TOPICS_FOLLOWED.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicFollowings);
    case 'TOPIC.CREATE_AND_CURATE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicFollowings);
    case 'TOPICS_ALL.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicFollowings);
    case 'TOPICS_ALL.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicFollowings);
    case 'TOPICS_CURATED.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicFollowings);
    case 'TOPICS_CURATED.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicFollowings);
    case 'SUGGESTED_TOPICS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicFollowings);
    case 'SUGGESTED_TOPICS.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicFollowings);
    case 'TYPEAHEAD.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicFollowings);
    case 'TOPIC.FOLLOW_FULFILLED':
      return followTopic(state, action.payload.topicId);
    case 'TOPIC.UNFOLLOW_FULFILLED':
      return unfollowTopic(state, action.payload.topicId);
    case 'TOPIC.FOLLOW_PENDING':
      return update(state, action.payload.topicId, { viewerIsFollowing: true });
    case 'TOPIC.UNFOLLOW_REJECTED':
      return update(state, action.payload.topicId, { viewerIsFollowing: true });
    case 'TOPIC.UNFOLLOW_PENDING':
      return update(state, action.payload.topicId, { viewerIsFollowing: false });
    case 'TOPIC.FOLLOW_REJECTED':
      return update(state, action.payload.topicId, { viewerIsFollowing: false });
    case 'CURATED_TOPIC.REMOVE_FULFILLED':
      return remove(state, [action.payload.topicId]);
    case 'TOPIC.REMOVE_FULFILLED':
      return removeTopic(state, action.payload);
    default:
      return state;
  }
};

export default reducer;
